import { createSlice } from '@reduxjs/toolkit'
import { isMac } from '~/lib/platform'

export const reservedShortcuts = [
	'Ctrl+N',
	'Ctrl+T',
	'Ctrl+W',
	'Ctrl+Shift+T',
	'Ctrl+L',
	'Ctrl+D',
	'Alt+D',
	'Ctrl+H',
	'Ctrl+J',
	'Ctrl+Y',
	'Alt+Left',
	'Alt+Right',
	'Ctrl+F',
	'F11',
	'Ctrl+Plus',
	'Ctrl+Minus',
	'F12',
]

export const defaultBindings = {
	// file
	new_project: 'Ctrl+Alt+N',
	open_project: 'Ctrl+O',
	save_project: 'Ctrl+S',
	// modal
	recordings: 'Ctrl+R',
	feedback: 'Ctrl+Shift+F',
	templates: 'Alt+Shift+A',
	settings_general: 'Ctrl+;',
	settings_shortcuts: 'Ctrl+Shift+K',
	// view
	show_ui: 'Q',
	// show_stats: 'S',
	show_presets: 'W',
	show_layers: 'L',
	// show_projects: 'P',
	show_source: '1',
	show_output: '2',
	show_minimal: '3',
	show_param_column: 'Shift+O',
	open_output_tab: '4',
	fullscreen: 'F',
	toggle_projection_map: 'M',
	// Ctrl+M on Mac reads as Cmd+M, which minimizes the window before the page sees it.
	learn: isMac ? 'Alt+M' : 'Ctrl+M',
	// tools
	move_tool: 'V',
	edit_path_tool: 'A',
	transform_tool: 'T',
	add_brush: 'B',
	add_path: 'P',
	add_circle: 'C',
	add_rect: 'R',
	eraser_tool: 'E',
	// objects
	group_objects: 'Ctrl+G',
	ungroup_objects: 'Ctrl+Shift+G',
	bringToFront: 'Ctrl+Up',
	sendToBack: 'Ctrl+Down',
	// edit
	undo: 'Ctrl+Z',
	redo: 'Ctrl+Shift+Z',
	copy: 'Ctrl+C',
	cut: 'Ctrl+X',
	paste: 'Ctrl+V',
	delete: 'Delete',
	rename: 'F2',
	// session
	capture_image: 'S',
	// presets
	focus_preset_name_input: 'N',
	add_preset: 'Ctrl+Enter',
	save_preset: 'Shift+Enter',
	prev_preset: '[',
	next_preset: ']',
	next_preset_instant: 'Alt+]',
	prev_preset_instant: 'Alt+[',
	// playback
	// Space is the transport for whatever is playing — the video file when one is loaded, autoplay otherwise (`toggle_transport`). Ctrl+Space is autoplay unconditionally, so the playlist stays reachable over a video.
	toggle_transport: 'Space',
	toggle_playback: 'Ctrl+Space',
	// One key, no modifier: a tap is struck in rhythm.
	tap_tempo: 'X',
	downbeat: 'Shift+D',
	// video
	video_seek_back: 'ArrowLeft',
	video_seek_forward: 'ArrowRight',
	video_seek_back_5: 'Ctrl+ArrowLeft',
	video_seek_forward_5: 'Ctrl+ArrowRight',
	video_frame_prev: 'Shift+ArrowLeft',
	video_frame_next: 'Shift+ArrowRight',
	video_mark_in: 'I',
	video_mark_out: 'O',
	// // session start_session: 'Ctrl+Shift+Y', stop_session: 'Ctrl+Shift+Q', debug
	copy_session_arn: 'Ctrl+Shift+C',
}

const keyBindingsSlice = createSlice({
	name: 'key_bindings',
	initialState: {
		bindings: { ...defaultBindings },
		conflicts: [], // Track any conflicting bindings
		editing: null, // Track which binding is being edited
	},
	reducers: {
		updateBinding: (state, { payload: { action, hotkey } }) => {
			// check if hotkey is reserved
			if (reservedShortcuts.includes(hotkey)) {
				return
			}
			if (hotkey) {
				// Remove any existing bindings using this hotkey
				Object.entries(state.bindings).forEach(([key, binding]) => {
					if (binding === hotkey && key !== action) {
						delete state.bindings[key]
					}
				})

				state.bindings[action] = hotkey
			} else {
				delete state.bindings[action]
			}
		},
		resetBinding: (state, { payload: action }) => {
			const d = defaultBindings[action]
			if (d) state.bindings[action] = d
			else delete state.bindings[action]
		},
		/** The whole map, as Learn's undo restores it. */
		setKeyBindings: (state, { payload }) => {
			state.bindings = { ...payload }
		},
		resetAllBindings: state => {
			state.bindings = { ...defaultBindings }
		},
		setEditing: (state, { payload }) => {
			state.editing = payload
		},
		validateBindings: state => {
			// Clear previous conflicts
			state.conflicts = []

			// Build a map of hotkeys to actions
			const hotkeyMap = {}
			Object.entries(state.bindings).forEach(([action, hotkey]) => {
				if (!hotkeyMap[hotkey]) {
					hotkeyMap[hotkey] = []
				}
				hotkeyMap[hotkey].push(action)
			})

			// Find conflicts (any hotkey with multiple actions)
			Object.entries(hotkeyMap).forEach(([hotkey, boundActions]) => {
				if (boundActions.length > 1) {
					state.conflicts.push({
						hotkey,
						actions: boundActions,
					})
				}
			})
		},
	},
})

export const { updateBinding, resetBinding, resetAllBindings, setKeyBindings, setEditing, validateBindings } =
	keyBindingsSlice.actions

export default keyBindingsSlice.reducer
